import React from 'react';

import {
  NavLink
} from 'react-router-dom';

import {
  motion
} from 'motion/react';

import {
  LayoutDashboard,
  FolderKanban,
  Bot,
  MessageSquareText,
  Terminal,
  Files,
  CloudUpload,
  History,
  Settings,
  ShieldCheck,
  Wifi
} from 'lucide-react';
import { MonitorUp } from 'lucide-react';

import {
  usePrivacy
} from '../../contexts/PrivacyContext';

import {
  useRealtime
} from '../../contexts/RealtimeContext';

import {
  AnimatedNumber
} from '../common/AnimatedNumber';

import {
  kaizenMicroTransition,
  kaizenNavTransition
} from '../../lib/kaizenMotion';


export const Sidebar:
React.FC = () => {

  const {
    isPrivacyMode
  } =
    usePrivacy();


  const {
    deviceStatus,
    latencyMs
  } =
    useRealtime();


  const navItems = [

    {
      name:
        'Overview',

      to:
        '/',

      icon:
        LayoutDashboard
    },

    {
      name:
        'Projects',

      to:
        '/projects',

      icon:
        FolderKanban
    },

    {
      name:
        'Console',

      to:
        '/console',

      icon:
        Terminal
    },

    {
      name:
        'JARVIS',

      to:
        '/assistant',

      icon:
        Bot
    },

    {
      name:
        'ChatGPT',

      to:
        '/chatgpt',

      icon:
        MessageSquareText
    },
    // KAIZEN_REMOTE_DESKTOP_NAV_FINAL
    {
      name:
        'Remote Desktop',

      to:
        '/remote-desktop',

      icon:
        MonitorUp
    },

    {
      name:
        'Files',

      to:
        '/files',

      icon:
        Files
    },

    {
      name:
        'Deployments',

      to:
        '/deployments',

      icon:
        CloudUpload
    },

    {
      name:
        'History',

      to:
        '/history',

      icon:
        History
    },

    {
      name:
        'Settings',

      to:
        '/settings',

      icon:
        Settings
    }

  ];


  const online =
    deviceStatus ===
      'online';


  return (

    <aside
      className="
        hidden
        md:flex
        w-[228px]
        shrink-0
        flex-col
        justify-between
        p-3
        border-r
        border-white/[0.055]
        bg-slate-950/34
        backdrop-blur-xl
        select-none
      "
    >

      <div>

        <div
          className="
            px-3
            pt-2
            pb-3
            text-[9px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-slate-600
          "
        >

          Navigation

        </div>


        <div className="space-y-1">

          {navItems.map(
            item => {

              const Icon =
                item.icon;


              return (

                <NavLink
                  key={
                    item.to
                  }
                  to={
                    item.to
                  }
                  end={
                    item.to ===
                    '/'
                  }
                  className={({
                    isActive
                  }) =>
                    `
                      group
                      relative
                      min-h-[40px]
                      flex
                      items-center
                      gap-3
                      px-3
                      rounded-xl
                      border
                      text-xs
                      font-medium
                      transition-all
                      ${
                        isActive
                          ? `
                              border-indigo-400/15
                              bg-indigo-400/[0.075]
                              text-indigo-200
                              shadow-[inset_0_1px_0_rgba(255,255,255,0.025),0_8px_26px_rgba(79,70,229,0.06)]
                            `
                          : `
                              border-transparent
                              text-slate-500
                              hover:text-slate-200
                              hover:bg-white/[0.035]
                              hover:border-white/[0.04]
                            `
                      }
                    `
                  }
                >

                  {({
                    isActive
                  }) => (

                    <>

                      {isActive && (

                        <span
                          className="
                            absolute
                            left-0
                            top-1/2
                            -translate-y-1/2
                            w-[2px]
                            h-5
                          "
                        >

                          <motion.span
                            layoutId="kaizen-sidebar-active-rail"
                            transition={
                              kaizenNavTransition
                            }
                            className="
                              block
                              w-full
                              h-full
                              rounded-r-full
                              bg-indigo-400
                              shadow-[0_0_12px_rgba(129,140,248,0.5)]
                            "
                          />

                        </span>

                      )}


                      <motion.span
                        className="
                          inline-flex
                          shrink-0
                        "
                        animate={
                          isActive
                            ? {
                                scale: 1.06,
                                x: 1
                              }
                            : {
                                scale: 1,
                                x: 0
                              }
                        }
                        transition={
                          kaizenMicroTransition
                        }
                      >

                        <Icon
                          className={`
                            w-4
                            h-4
                            shrink-0
                            ${
                              isActive
                                ? 'text-indigo-300'
                                : 'text-slate-600 group-hover:text-slate-300'
                            }
                          `}
                        />

                      </motion.span>


                      <span>
                        {item.name}
                      </span>

                    </>

                  )}

                </NavLink>

              );
            }
          )}

        </div>

      </div>


      {/* --------------------------------------------------
          HOST STATUS
      -------------------------------------------------- */}

      <div
        className="
          overflow-hidden
          rounded-xl
          border
          border-white/[0.06]
          bg-white/[0.025]
        "
      >

        <div
          className="
            px-3
            py-2.5
            border-b
            border-white/[0.05]
          "
        >

          <div
            className="
              flex
              items-center
              justify-between
              gap-2
              text-[10px]
            "
          >

            <span
              className="
                flex
                items-center
                gap-1.5
                text-slate-500
              "
            >

              <Wifi className="w-3 h-3" />

              Host Link

            </span>


            <span
              className={
                online
                  ? 'text-emerald-300 font-medium'
                  : 'text-slate-600'
              }
            >

              {online
                ? 'Connected'
                : 'Standby'}

            </span>

          </div>


          <div
            className="
              mt-1
              flex
              items-center
              justify-between
              gap-2
              text-[9px]
            "
          >

            <span className="text-slate-600">
              Round trip
            </span>

            <span className="font-mono text-slate-500">
              <AnimatedNumber
                value={latencyMs}
                suffix="ms"
              />
            </span>

          </div>

        </div>


        <div
          className="
            px-3
            py-2.5
            flex
            items-center
            justify-between
            gap-2
            text-[10px]
          "
        >

          <span
            className="
              flex
              items-center
              gap-1.5
              text-slate-500
            "
          >

            <ShieldCheck className="w-3 h-3" />

            Privacy

          </span>


          <span
            className={
              isPrivacyMode
                ? 'text-indigo-300 font-medium'
                : 'text-slate-600'
            }
          >

            {isPrivacyMode
              ? 'Active'
              : 'Off'}

          </span>

        </div>

      </div>

    </aside>
  );
};
